/**
 * Pure merge rules the store adds on top of core's `mergeCatalog`. No storage, no clocks.
 *
 *   isNewer         last-writer-wins for sync and import; timestamps compared with `timeOf`
 *                   (rule 6), never as strings; a same-millisecond tie broken exactly as the
 *                   worker breaks it (worker/src/sync/merge.ts), so devices converge.
 *   userTable       the person's own category ratings as an AvailabilityTable (source 'user'),
 *                   for mergeCatalog, which resolves user > community > curated per cell.
 *   applyFoodRatings the person's own per-food ratings onto `Food.availability`.
 */
import type { AvailabilityRating, AvailabilityTable, Food } from '@resupport/core'
import { canonicalJson, timeOf } from '../engine'

export { canonicalJson }
import type { UserRating } from './types'

interface Stamped {
  id?: string
  updated_at?: string
  deleted?: boolean
}

/**
 * A record's content as the worker compares it on a tie: the sync record's `deleted` flag and
 * `data`, where a tombstone's data is only `{ id }` (the worker stores no more than that).
 */
export function syncContent(v: Stamped): string {
  const deleted = v.deleted === true
  return canonicalJson({ deleted, data: deleted ? { id: v.id } : v })
}

/**
 * True when `a` wins over `b` under last-writer-wins. A missing `b` is older than anything.
 * Equal stamps with different content go to the greater canonical JSON, as on the worker, so
 * every device keeps the same copy whatever order the two arrive in.
 */
export function isNewer(a: Stamped, b: Stamped | undefined): boolean {
  if (!b) return true
  const ta = timeOf(a.updated_at)
  const tb = timeOf(b.updated_at)
  if (ta !== tb) return ta > tb
  return syncContent(a) > syncContent(b)
}

/** A user rating as the engine sees it. */
export function ratingOf(r: UserRating): AvailabilityRating {
  return {
    level: r.level,
    source: 'user',
    updated_at: r.updated_at,
    ...(r.price ? { price: r.price } : {}),
    ...(r.note ? { note: r.note } : {}),
  }
}

/** Live category-level user ratings as a table. Per-food ratings are left to applyFoodRatings. */
export function userTable(ratings: Iterable<UserRating>): AvailabilityTable {
  const out: AvailabilityTable = {}
  for (const r of ratings) {
    if (r.deleted || r.food) continue
    out[r.category] = { ...(out[r.category] ?? {}), [r.region]: ratingOf(r) }
  }
  return out
}

/** Community ratings re-labelled as such, whatever the server called them. */
export function communityTable(table: AvailabilityTable): AvailabilityTable {
  const out: AvailabilityTable = {}
  for (const [cat, byRegion] of Object.entries(table)) {
    const cell: Record<string, AvailabilityRating> = {}
    for (const [region, rating] of Object.entries(byRegion)) {
      cell[region] = { ...rating, source: 'community' }
    }
    out[cat] = cell
  }
  return out
}

/** Merge two tables cell by cell; `over` wins where both have a cell. */
export function layerTables(under: AvailabilityTable, over: AvailabilityTable): AvailabilityTable {
  const out: AvailabilityTable = { ...under }
  for (const [cat, byRegion] of Object.entries(over)) {
    out[cat] = { ...(out[cat] ?? {}), ...byRegion }
  }
  return out
}

/**
 * The foods map with the person's own per-food ratings laid onto `Food.availability`.
 * Returns the same map when there are none.
 */
export function applyFoodRatings(
  foods: Map<string, Food>,
  ratings: Iterable<UserRating>,
): Map<string, Food> {
  const byFood = new Map<string, UserRating[]>()
  for (const r of ratings) {
    if (r.deleted || !r.food) continue
    byFood.set(r.food, [...(byFood.get(r.food) ?? []), r])
  }
  if (byFood.size === 0) return foods
  const out = new Map(foods)
  for (const [id, mine] of byFood) {
    const food = out.get(id)
    if (!food) continue
    const availability = { ...(food.availability ?? {}) }
    for (const r of mine) availability[r.region] = ratingOf(r)
    out.set(id, { ...food, availability })
  }
  return out
}

/**
 * A reset marker in the user layer: `{ id, reset: true, updated_at }`. It stands for "no
 * overlay" (core `mergeCatalog` keeps the shipped record) and exists so the reset syncs and
 * outranks older user copies by `updated_at`. Never shown, never exported.
 */
export function isResetMarker(v: unknown): v is { id: string; reset: true; updated_at?: string } {
  return (
    typeof v === 'object' &&
    v !== null &&
    (v as { reset?: unknown }).reset === true &&
    typeof (v as { id?: unknown }).id === 'string'
  )
}

/**
 * The person's own live copy of a food or recipe, if there is one: not a soft delete and not
 * a reset marker. Use this, not `map.has(id)`, for "edited" badges and "your copy" notes.
 */
export function userCopyOf<T extends { deleted?: boolean; reset?: boolean }>(
  layer: ReadonlyMap<string, T>,
  id: string,
): T | undefined {
  const v = layer.get(id)
  return v && !v.deleted && !v.reset ? v : undefined
}

/**
 * A food's availability without the person's own per-food ratings, which `applyFoodRatings`
 * lays on for display (source 'user'). Those live as ratings; persisting them inside the food
 * would freeze a copy that clearing the rating could not remove.
 */
export function stripUserAvailability(food: Food): Food {
  if (!food.availability) return food
  const kept = Object.entries(food.availability).filter(([, r]) => r.source !== 'user')
  if (kept.length === Object.keys(food.availability).length) return food
  const { availability: _all, ...rest } = food
  return kept.length > 0 ? { ...rest, availability: Object.fromEntries(kept) } : rest
}
